import React, { memo, useCallback, useState } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { IntegrationBusinessAction } from "actions";
import { IntegrationCredentialAction } from "actions/integrationCredentials";
import AgGridContainer from "components/AgGrid/AgGridContainer";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { Button } from "components/Form";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import MessageBox from "components/MessageBox";

import { AgGridTables } from "constants/aggrid";
import { IntegrationBusinessColumnDefinitions } from "constants/columnDefinitions/integrationBusiness";
import { IntegrationTypes } from "constants/integrations";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";

import {
  openCreateIntegrationBusinessModal,
  openHelpAndSupport,
  openIntegrationSettings,
} from "lib/navigation";

import {
  getBusinessById,
  getContextByModalType,
  getDefaultBillingSettings,
  getIntegrationBusinessesByType,
} from "selectors";

import {
  useCanLinkExternalIntegrationPermission,
  useDebounceSelector,
  useHasExternalIntegrationsPermission,
} from "hooks";

import { Footer } from "./Footer";

const ContactLink = styled.a`
  cursor: pointer;
  text-decoration: underline;
`;

interface IntegrationCredentialsButtonProps {
  integrationCredentialType: string;
}

function IntegrationCredentialsButton({
  integrationCredentialType,
}: IntegrationCredentialsButtonProps) {
  const dispatch = useDispatch();
  const billingSettings = useSelector(getDefaultBillingSettings);
  const xeroIntegrationCredentialId =
    billingSettings?.xeroIntegrationCredentialId || null;

  function onClickRefresh() {
    dispatch(
      IntegrationCredentialAction.synchronize(xeroIntegrationCredentialId),
    );
  }

  if (xeroIntegrationCredentialId) {
    return (
      <WaitForSync requiredData={[ApiModel.INTEGRATION_CREDENTIALS]}>
        <Button inline type="button" onClick={onClickRefresh}>
          Refresh {integrationCredentialType} Businesses
        </Button>
      </WaitForSync>
    );
  }
  return (
    <WaitForSync requiredData={[ApiModel.INTEGRATION_CREDENTIALS]}>
      <Button inline type="button" onClick={openIntegrationSettings}>
        Add {integrationCredentialType} Credentials
      </Button>
    </WaitForSync>
  );
}

interface EditIntegrationBusinessModalTitleProps {
  integrationCredentialType: string;
  businessId: string;
  onClose: () => void;
}

function EditIntegrationBusinessModalTitleComponent({
  integrationCredentialType,
  businessId,
  onClose,
}: EditIntegrationBusinessModalTitleProps) {
  const { name } = useSelector(getBusinessById(businessId)) || {};

  return (
    <DialogTitle onClose={onClose}>
      <Row justifyBetween>
        Link {IntegrationTypes[integrationCredentialType]} Business
        {name && ` to ${name}`}
      </Row>
    </DialogTitle>
  );
}

const EditIntegrationBusinessModalTitle = memo(
  EditIntegrationBusinessModalTitleComponent,
);

const getRowId = (params: { data: { id: number } }) => String(params.data.id);

interface ModalContext {
  integrationCredentialType: string;
  businessId: string;
}

interface EditIntegrationBusinessModalProps {
  onClose: () => void;
}

export function EditIntegrationBusinessModal({
  onClose,
}: EditIntegrationBusinessModalProps) {
  const context = useSelector<RootState, ModalContext | null>(
    getContextByModalType(ModalTypes.EditIntegrationBusiness),
  );

  const { integrationCredentialType, businessId } = context || {};

  const rowData = useDebounceSelector(
    getIntegrationBusinessesByType(integrationCredentialType),
  );

  const hasFeaturePermission = useHasExternalIntegrationsPermission();
  const dispatch = useDispatch();
  const [integrationBusinessId, setIntegrationBusinessId] = useState<
    number | null
  >(null);
  const canLinkExternalIntegration = useCanLinkExternalIntegrationPermission();

  function onSubmitForm() {
    dispatch(
      IntegrationBusinessAction.linkToBusiness(
        integrationBusinessId,
        businessId,
      ),
    );
    onClose();
  }

  const onSelectionChanged = useCallback((selectedRows: { id: number }[]) => {
    setIntegrationBusinessId(
      selectedRows.length > 0 ? selectedRows[0].id : null,
    );
  }, []);

  const isRowSelectable = ({ data }: { data?: { businessId?: string } }) =>
    data != null && !data.businessId;

  const createNew = () =>
    openCreateIntegrationBusinessModal(
      integrationCredentialType,
      businessId,
      window.location.hash,
    );

  function onClickUnlink() {
    dispatch(
      IntegrationBusinessAction.unlinkFromBusiness(
        integrationBusinessId,
        businessId,
      ),
    );
  }

  const columnDefs = React.useMemo(
    () => Object.values(IntegrationBusinessColumnDefinitions),
    [],
  );

  return (
    <Dialog fullWidth fullScreen maxWidth="lg" open onClose={onClose}>
      <EditIntegrationBusinessModalTitle
        integrationCredentialType={integrationCredentialType}
        businessId={businessId}
        onClose={onClose}
      />
      <form onSubmit={onSubmitForm}>
        <DialogContent dividers>
          <WaitForSync requiredData={[ApiModel.DEPLOYMENTS]}>
            {hasFeaturePermission ? (
              <Column fullWidth fullHeight>
                {canLinkExternalIntegration && (
                  <Row justifyBetween alignCenter padding={1}>
                    <div>
                      Select a {integrationCredentialType} business below to
                      link.
                    </div>
                    <div>
                      <IntegrationCredentialsButton
                        integrationCredentialType={integrationCredentialType}
                      />
                    </div>
                  </Row>
                )}
                <AgGridContainer
                  WrapperComponent={ModalTableWrapper}
                  columnDefs={columnDefs}
                  getRowId={getRowId}
                  onRowSelectionChanged={onSelectionChanged}
                  pagination
                  paginationAutoPageSize
                  rowData={rowData}
                  isRowSelectable={isRowSelectable}
                  tableName={AgGridTables.INTEGRATION_BUSINESSES}
                />
              </Column>
            ) : (
              <MessageBox>
                <FontAwesomeIcon icon={faLock} />
                &nbsp;
                <div>
                  This feature is currently unavailable.
                  <br />
                  Please{" "}
                  <ContactLink onClick={openHelpAndSupport}>
                    contact AgriNous
                  </ContactLink>{" "}
                  to discuss options with external integration.
                </div>
              </MessageBox>
            )}
          </WaitForSync>
        </DialogContent>
        <DialogActions>
          <Footer
            integrationBusinessId={integrationBusinessId}
            businessId={businessId}
            integrationCredentialType={integrationCredentialType}
            hasFeaturePermission={hasFeaturePermission}
            onClose={onClose}
            onClickUnlink={onClickUnlink}
            createNew={createNew}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
